/**
 * Firestore CRUD for agent-proposed draft take-off links (biddingTakeoffProposals).
 *
 * The Mac-mini bidding agent writes proposals (status: pending) via the API.
 * CRM staff approve them here — approval creates a real pdfTakeoffLink and marks
 * the proposal approved. Agents never push take-off to proposals without approval.
 */

import { db } from './firebase';
import {
  collection,
  doc,
  updateDoc,
  onSnapshot,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import type { BiddingTakeoffProposal, BiddingProposedLink, PdfTakeoffLink } from '../types';
import { createTakeoffLink } from './pdfTakeoffLinkService';

const COLLECTION = 'biddingTakeoffProposals';

function fromDoc(snap: { id: string; data: () => Record<string, unknown> }): BiddingTakeoffProposal {
  const d = snap.data();
  const created = d.createdAt as { toDate?: () => Date } | undefined;
  const reviewed = d.reviewedAt as { toDate?: () => Date } | undefined;
  return {
    id: snap.id,
    leadId: d.leadId as string,
    dealId: (d.dealId as string) ?? null,
    documentId: d.documentId as string,
    documentUrl: (d.documentUrl as string) || (d.documentId as string),
    pageNumber: typeof d.pageNumber === 'number' ? d.pageNumber : 1,
    analysisId: (d.analysisId as string) || '',
    status: (d.status as BiddingTakeoffProposal['status']) || 'pending',
    proposedLink: (d.proposedLink as BiddingProposedLink) || {},
    confidence: typeof d.confidence === 'number' ? d.confidence : undefined,
    rationale: (d.rationale as string) || undefined,
    annotationId: (d.annotationId as string) || undefined,
    source: 'biddingAgent',
    createdByUserId: (d.createdByUserId as string) || undefined,
    createdByUserName: (d.createdByUserName as string) || undefined,
    createdAt: created?.toDate?.() ?? undefined,
    reviewedAt: reviewed?.toDate?.() ?? undefined,
    reviewedBy: (d.reviewedBy as string) || undefined,
    createdLinkId: (d.createdLinkId as string) || undefined,
  };
}

/** Subscribe to a lead's take-off proposals (optionally only pending), newest first. */
export function subscribeLeadTakeoffProposals(
  leadId: string,
  callback: (list: BiddingTakeoffProposal[]) => void,
  options?: { pendingOnly?: boolean },
): () => void {
  const q = query(collection(db, COLLECTION), where('leadId', '==', leadId));
  return onSnapshot(q, snap => {
    let list = snap.docs.map(d => fromDoc({ id: d.id, data: () => d.data() }));
    if (options?.pendingOnly) list = list.filter(p => p.status === 'pending');
    list.sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
    callback(list);
  });
}

interface ReviewerInfo {
  userId: string;
  userName: string;
}

/**
 * Approve a proposal: create a real pdfTakeoffLink from the proposed values and
 * mark the proposal approved. Returns the created take-off link id.
 */
export async function approveTakeoffProposal(
  proposal: BiddingTakeoffProposal,
  reviewer: ReviewerInfo,
): Promise<string> {
  if (!proposal.id) throw new Error('Proposal id required');
  const link = proposal.proposedLink || {};
  const linkInput: Omit<PdfTakeoffLink, 'id' | 'createdAt' | 'updatedAt'> = {
    documentId: proposal.documentId,
    documentUrl: proposal.documentUrl || proposal.documentId,
    dealId: proposal.dealId || '',
    leadId: proposal.leadId,
    measurementId: link.measurementId || '',
    pageNumber: proposal.pageNumber || 1,
    structureId: link.structureId || '',
    structureName: link.structureName || 'Agent take-off',
    timberItemId: link.timberItemId || undefined,
    name: link.name || '',
    species: link.species || '',
    grade: link.grade || '',
    dimensions: link.dimensions || '',
    quantity: typeof link.quantity === 'number' ? link.quantity : 1,
    moistureContent: link.moistureContent || '',
    measuredLength: link.measuredLength,
    measuredLengthUnit: link.measuredLengthUnit,
    unitCost: link.unitCost,
    notes: link.notes,
    userId: reviewer.userId,
    userName: reviewer.userName,
  };
  const linkId = await createTakeoffLink(linkInput);
  await updateDoc(doc(db, COLLECTION, proposal.id), {
    status: 'approved',
    reviewedAt: Timestamp.now(),
    reviewedBy: reviewer.userId,
    createdLinkId: linkId,
  });
  return linkId;
}

/** Reject a proposal without creating a take-off link. */
export async function rejectTakeoffProposal(proposalId: string, reviewer: ReviewerInfo): Promise<void> {
  await updateDoc(doc(db, COLLECTION, proposalId), {
    status: 'rejected',
    reviewedAt: Timestamp.now(),
    reviewedBy: reviewer.userId,
  });
}
